import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LatLngTuple } from 'leaflet';

import { useAddVulOnMapModalStore } from '../components/Modals/AddVulOnMap/AddVulOnMapModalStore';
import { useAddEncounterOnMapModalStore } from '../components/Modals/AddEncOnMap/AddEncounterOnMapModalStore';
import { useCompleteEncModalStore } from '../components/Modals/CompleteEncModal/CompleteEncModalStore';
import { usePutEncAfterBlitzModalStore } from '../components/Modals/PutEncAfterBlitzModal/PutEncAfterBlitzModalStore';

import { useGetPlanList } from 'hooks/InteractiveMap/useGetPlanList';
import { getDevicesOnMap } from 'api/InteractiveMap/getDevicesOnMap';
import { getHubsOnMap } from 'api/InteractiveMap/getHubsOnMap';
import { getVulnerabilitiesOnMap } from 'api/InteractiveMap/getVulnerabilities';
import { getEncountersOnMap } from 'api/InteractiveMap/getEncountersOnMap';
import { useTrapDeleteModalStore } from 'components/DeleteTrapModal/TrapsDeleteModal';
import { useHubDeleteModalStore } from 'components/DeleteHubModal/HubsDeleteStore';
import { DevicesOnMapResponse, ArchiveDevicesResponse } from 'api/InteractiveMap/types';
import { getLocationsOnMap } from 'api/InteractiveMap/getLocationOnMap';
import { MapLocation } from 'api/traps/types';
import { getFullArchiveOnMap } from 'api/InteractiveMap/getFullArchiveOnMap';

export const useMapState = () => {
  const { slug, planSlug } = useParams();
  const navigate = useNavigate();
  const [mapHeight, setMapHeight] = useState<number>(window.innerHeight - 270);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [selectedPlanSlug, setSelectedPlanSlug] = useState<string>('');
  const [previousSlug, setPreviousSlug] = useState<string>(slug || '');
  const [devices, setDevices] = useState<DevicesOnMapResponse | ArchiveDevicesResponse>({
    mct: [],
    srbm: [],
    smst: [],
    srst: [],
  });
  const [hubs, setHubs] = useState<any[]>([]);
  const [hubsLoading, setHubsLoading] = useState(false);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [isAddMctModalOpen, setIsAddMctModalOpen] = useState(false);
  const [isAddSMSTModalOpen, setIsAddSMSTModalOpen] = useState(false);
  const [isAddSRSTModalOpen, setIsAddSRSTModalOpen] = useState(false);
  const [isAddSRBMModalOpen, setIsAddSRBMModalOpen] = useState(false);
  const [isAddHubModalOpen, setIsAddHubModalOpen] = useState(false);
  const [vulnerabilities, setVulnerabilities] = useState<any[]>([]);
  const [vulnerabilitiesLoading, setVulnerabilitiesLoading] = useState(false);
  const [encounters, setEncounters] = useState<any[]>([]);
  const [encountersLoading, setEncountersLoading] = useState(false);
  const [isHeatmapPopupOpen, setIsHeatmapPopupOpen] = useState(false);
  const [isLocationSettingsOpen, setIsLocationSettingsOpen] = useState(false);
  const [tempPolygon, setTempPolygon] = useState<LatLngTuple[]>([]);
  const [polygons, setPolygons] = useState<MapLocation[]>([]);
  const [colorPolygon, setColorPolygon] = useState<string>('#800080');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [isActiveFilterArchive, setIsActiveFilterArchive] = useState(false);
  const [archiveMode, setArchiveMode] = useState<'date' | 'period'>('date');
  const [heatmapDateStart, setHeatmapDateStart] = useState(() => {
    const today = new Date();
    today.setMonth(today.getMonth() - 1);
    return today.toISOString().slice(0, 10);
  });
  const [heatmapDateEnd, setHeatmapDateEnd] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });

  const { isOpen: isAddVulModalOpen } = useAddVulOnMapModalStore();
  const { isOpen: isAddEncounterModalOpen } = useAddEncounterOnMapModalStore();
  const { isOpen: isTrapDeleteModalOpen } = useTrapDeleteModalStore();
  const { isOpen: isHubDeleteModalOpen } = useHubDeleteModalStore();
  const { isOpen: isCompleteEncModalOpen } = useCompleteEncModalStore();
  const { isOpen: isPutEncAfterBlitzOpen } = usePutEncAfterBlitzModalStore();
  // Check if any modal is open
  const isAnyModalOpen =
    isAddMctModalOpen ||
    isAddSMSTModalOpen ||
    isAddSRSTModalOpen ||
    isAddSRBMModalOpen ||
    isAddHubModalOpen ||
    isAddVulModalOpen ||
    isAddEncounterModalOpen ||
    isTrapDeleteModalOpen ||
    isHubDeleteModalOpen ||
    isCompleteEncModalOpen ||
    isPutEncAfterBlitzOpen;

  const {
    data: plans,
    isLoading: plansLoading,
    refetch: refetchPlans,
    isRefetching: isRefetchingPlans,
  } = useGetPlanList(slug || '');

  const currentPlan = plans?.find(plan => plan.slug === (planSlug || selectedPlanSlug)) || plans?.[0];

  const fetchHubs = useCallback(async () => {
    if (!currentPlan) return;
    setHubsLoading(true);
    try {
      const data = await getHubsOnMap(currentPlan!.slug);
      setHubs(data || []);
    } finally {
      setHubsLoading(false);
    }
  }, [currentPlan]);

  const fetchLocations = useCallback(async () => {
    if (!currentPlan) return;
    try {
      const data = await getLocationsOnMap(currentPlan!.slug);
      setPolygons(data || []);
    } finally {
    }
  }, [currentPlan]);

  const fetchArchiveData = useCallback(async () => {
    if (!currentPlan) return;

    setDevices({ mct: [], srbm: [], smst: [], srst: [] });
    setVulnerabilities([]);
    setEncounters([]);

    setDevicesLoading(true);
    setVulnerabilitiesLoading(true);
    setEncountersLoading(true);
    try {
      const archiveParams = {
        floor_slug: currentPlan.slug,
        mode: archiveMode,
        date_value: archiveMode === 'date' ? heatmapDateStart : undefined,
        start_date: archiveMode === 'period' ? heatmapDateStart : undefined,
        end_date: archiveMode === 'period' ? heatmapDateEnd : undefined,
      };

      const data = await getFullArchiveOnMap(archiveParams);

      setDevices(data.traps || { mct: [], srbm: [], smst: [], srst: [] });
      setVulnerabilities(data.markers?.vulnerabilities || []);
      setEncounters(data.markers?.encounters || []);
    } catch (error) {
      console.error('Error fetching archive data:', error);
    } finally {
      setDevicesLoading(false);
      setVulnerabilitiesLoading(false);
      setEncountersLoading(false);
    }
  }, [currentPlan, archiveMode, heatmapDateStart, heatmapDateEnd]);

  const fetchDevices = useCallback(async () => {
    if (!currentPlan || isActiveFilterArchive) return;
    setDevicesLoading(true);
    try {
      const data = await getDevicesOnMap(currentPlan.slug);
      setDevices(data || { mct: [], srbm: [], smst: [], srst: [] });
    } finally {
      setDevicesLoading(false);
    }
  }, [currentPlan, isActiveFilterArchive]);

  const fetchVulnerabilities = useCallback(async () => {
    if (!currentPlan || isActiveFilterArchive) return;
    setVulnerabilitiesLoading(true);
    try {
      const data = await getVulnerabilitiesOnMap(currentPlan.slug);
      setVulnerabilities(data || []);
    } finally {
      setVulnerabilitiesLoading(false);
    }
  }, [currentPlan, isActiveFilterArchive]);

  const fetchEncounters = useCallback(async () => {
    if (!currentPlan || isActiveFilterArchive) return;
    setEncountersLoading(true);
    try {
      const data = await getEncountersOnMap(currentPlan.slug);
      setEncounters(data || []);
    } finally {
      setEncountersLoading(false);
    }
  }, [currentPlan, isActiveFilterArchive]);

  useEffect(() => {
    if (slug !== previousSlug) {
      setPreviousSlug(slug || '');
      setSelectedPlanSlug('');
      setDevices({ mct: [], srbm: [], smst: [], srst: [] });
      setHubs([]);
      setVulnerabilities([]);
      setEncounters([]);
      setPolygons([]);
      refetchPlans();
      setIsHeatmapPopupOpen(false);
    }
  }, [slug, previousSlug, refetchPlans]);

  useEffect(() => {
    if (isActiveFilterArchive) {
      fetchArchiveData();
    }
  }, [isActiveFilterArchive, fetchArchiveData]);

  useEffect(() => {
    if (!isActiveFilterArchive && currentPlan?.slug) {
      fetchDevices();
      fetchVulnerabilities();
      fetchEncounters();
    } else if (isActiveFilterArchive) {
      setDevices({ mct: [], srbm: [], smst: [], srst: [] });
      setVulnerabilities([]);
      setEncounters([]);
    }
  }, [isActiveFilterArchive, currentPlan?.slug, fetchDevices, fetchVulnerabilities, fetchEncounters]);

  useEffect(() => {
    if (currentPlan?.slug) {
      fetchHubs();
      fetchLocations();
    }
  }, [currentPlan?.slug, fetchHubs, fetchLocations]);

  useEffect(() => {
    if (!currentPlan) return;

    setIsHeatmapPopupOpen(false);

    const img = new Image();
    img.onload = () => {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = () => {
      console.error('Error loading image:', img.src);
      setImageDimensions({ width: 800, height: 600 });
    };
    img.src =
      currentPlan.floor_plan ||
      'https://borisstudio.com/wp-content/webp-express/webp-images/uploads/linejnaya-1.png.webp';
  }, [currentPlan]);

  useEffect(() => {
    let t: number | undefined;
    const handleResize = () => {
      if (document.visibilityState !== 'visible') return;
      window.clearTimeout(t);
      t = window.setTimeout(() => {
        setMapHeight(window.innerHeight - 270);
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.clearTimeout(t);
    };
  }, []);

  const handlePlanSelect = (newPlanSlug: string) => {
    setSelectedPlanSlug(newPlanSlug);
    navigate(`/object/interactive-map/${slug}/${newPlanSlug}`);
  };

  return {
    mapHeight,
    imageDimensions,
    plans,
    currentPlan,
    devices,
    hubs,
    setHubs,
    hubsLoading,
    plansLoading,
    isRefetchingPlans,
    devicesLoading,
    handlePlanSelect,
    selectedPlanSlug,
    isAddMctModalOpen,
    isAddSMSTModalOpen,
    isAddSRSTModalOpen,
    isAddSRBMModalOpen,
    setIsAddMctModalOpen,
    setIsAddSMSTModalOpen,
    setIsAddSRSTModalOpen,
    setIsAddSRBMModalOpen,
    slug,
    refetchDevices: fetchDevices,
    refetchHubs: fetchHubs,
    isAddHubModalOpen,
    setIsAddHubModalOpen,
    vulnerabilities,
    setVulnerabilities,
    vulnerabilitiesLoading,
    refetchVulnerabilities: fetchVulnerabilities,
    encounters,
    setEncounters,
    encountersLoading,
    refetchEncounters: fetchEncounters,
    refetchLocations: fetchLocations,
    isAnyModalOpen,
    isHeatmapPopupOpen,
    setIsHeatmapPopupOpen,
    isLocationSettingsOpen,
    setIsLocationSettingsOpen,
    tempPolygon,
    setTempPolygon,
    polygons,
    colorPolygon,
    setColorPolygon,
    editingIndex,
    setEditingIndex,
    isDrawingMode,
    setIsDrawingMode,
    setPolygons,
    isActiveFilterArchive,
    setIsActiveFilterArchive,
    heatmapDateStart,
    setHeatmapDateStart,
    heatmapDateEnd,
    setHeatmapDateEnd,
    archiveMode,
    setArchiveMode,
  };
};
